import React, { useState } from 'react';
import { XMarkIcon, CheckCircleIcon, BuildingOffice2Icon } from '@heroicons/react/24/outline';
import { subscriptionAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';

interface EnterpriseEnquiryModalProps {
  open: boolean;
  onClose: () => void;
}

const EnterpriseEnquiryModal: React.FC<EnterpriseEnquiryModalProps> = ({ open, onClose }) => {
  const { user } = useAuth();
  const fullName = [user?.first_name, user?.last_name].filter(Boolean).join(' ').trim();

  const [name, setName] = useState(fullName);
  const [email, setEmail] = useState(user?.email ?? '');
  const [company, setCompany] = useState('');
  const [phone, setPhone] = useState(user?.phone ?? '');
  const [expectedVolume, setExpectedVolume] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);

  if (!open) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) {
      setError('Name and email are required.');
      return;
    }
    try {
      setSubmitting(true);
      setError('');
      await subscriptionAPI.submitEnterpriseEnquiry({
        name: name.trim(),
        email: email.trim(),
        company: company.trim() || undefined,
        phone: phone.trim() || undefined,
        expected_volume: expectedVolume.trim() || undefined,
        message: message.trim() || undefined,
      });
      setSubmitted(true);
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass =
    'w-full px-3 py-2 rounded-lg bg-slate-900/60 border border-slate-600 text-white placeholder-slate-500 text-sm focus:outline-none focus:ring-2 focus:ring-violet-400 focus:border-transparent transition';

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto custom-scrollbar">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm transition-opacity" onClick={onClose}></div>

        <div className="relative bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl shadow-2xl border border-slate-700 max-w-lg w-full p-5 sm:p-6 transform transition-all">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors"
            aria-label="Close"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>

          {submitted ? (
            <div className="text-center py-6">
              <div className="flex justify-center mb-3">
                <div className="p-2.5 bg-emerald-500/10 rounded-full">
                  <CheckCircleIcon className="h-10 w-10 text-emerald-400" />
                </div>
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Thanks for reaching out</h3>
              <p className="text-gray-300 text-sm mb-5">
                Our sales team will be in touch shortly to tailor a plan to your volume.
              </p>
              <button
                onClick={onClose}
                className="px-5 py-2 bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-400 hover:to-purple-500 text-white rounded-lg transition-all font-medium text-sm"
              >
                Done
              </button>
            </div>
          ) : (
            <>
              <div className="flex justify-center mb-3">
                <div className="p-2.5 bg-violet-500/10 rounded-full">
                  <BuildingOffice2Icon className="h-10 w-10 text-violet-400" />
                </div>
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-white text-center mb-1">Talk to our sales team</h3>
              <p className="text-gray-400 text-center text-xs sm:text-sm mb-4">
                Tell us about your needs and we'll put together volume-based pricing for you.
              </p>

              {error && (
                <div className="mb-3 bg-red-500/20 border border-red-400/50 text-red-100 px-3 py-2 rounded-lg text-sm">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-slate-300 mb-1">Name *</label>
                    <input className={inputClass} value={name} onChange={(e) => setName(e.target.value)} required />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-300 mb-1">Work email *</label>
                    <input type="email" className={inputClass} value={email} onChange={(e) => setEmail(e.target.value)} required />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-300 mb-1">Company</label>
                    <input className={inputClass} value={company} onChange={(e) => setCompany(e.target.value)} />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-300 mb-1">Phone</label>
                    <input className={inputClass} value={phone} onChange={(e) => setPhone(e.target.value)} />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-300 mb-1">Expected monthly call volume</label>
                  <input
                    className={inputClass}
                    value={expectedVolume}
                    onChange={(e) => setExpectedVolume(e.target.value)}
                    placeholder="e.g. 10,000 calls / month"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-300 mb-1">How can we help?</label>
                  <textarea
                    className={`${inputClass} resize-none`}
                    rows={3}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Tell us about your use case, integrations, or compliance needs."
                  />
                </div>

                <div className="flex flex-col sm:flex-row gap-2 pt-1">
                  <button
                    type="button"
                    onClick={onClose}
                    className="flex-1 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors font-medium text-sm"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="flex-1 px-4 py-2 bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-400 hover:to-purple-500 text-white rounded-lg transition-all font-medium shadow-lg text-sm disabled:opacity-50"
                  >
                    {submitting ? 'Sending...' : 'Send enquiry'}
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default EnterpriseEnquiryModal;
